import { config } from "../Environment";

// ─── Types ───────────────────────────────────────────────────────────────────

export type TraitHierarchy = "domain" | "capability" | "behavior";
export type TraitCategory = "strength" | "value" | "aspiration" | "skill";
export type TraitSource =
  | "user_created"
  | "ai_suggested"
  | "mentor_assigned"
  | "library"
  | "autoenginuity";
export type AssessmentLevel = 0 | 1 | 2 | 3;
export type EvidenceRelationship = "develops" | "demonstrates" | "challenges";

export interface TraitEvidence {
  type: "insight" | "engagement" | "reflection" | "action_item" | "note";
  referenceId?: string;
  note?: string;
  relationship?: EvidenceRelationship;
  linkedBy?: "user" | "ai" | "system";
  date: string;
}

export interface AssessmentHistoryEntry {
  level: AssessmentLevel;
  note?: string;
  date: string;
}

export interface UserTrait {
  _id: string;
  fiUser: string;
  name: string;
  description?: string;
  category: TraitCategory;
  emoji?: string;
  spaceIds: string[];
  pwfIds?: string[];
  source: TraitSource;
  evidence: TraitEvidence[];

  // Hierarchy
  parentTraitId?: string | null;
  children: string[];
  hierarchy?: TraitHierarchy | null;

  // Self-assessment
  level: AssessmentLevel;
  assessmentHistory?: AssessmentHistoryEntry[];

  // Personal development
  personalDefinition?: string;
  personalExamples?: string[];
  developmentOpportunities?: string;

  // Prioritization
  priority?: number | null;
  isActive?: boolean;

  // Library reference
  templateSlug?: string | null;
  tags?: string[];

  createdOn: string;
  lastUpdated: string;

  // Computed fields (from tree query)
  effectiveLevel?: number;
  totalEvidence?: number;
}

/** A UserTrait with nested children (returned by listTraits with tree=true) */
export interface UserTraitTreeNode extends Omit<UserTrait, "children"> {
  children: UserTraitTreeNode[];
}

export interface CreateTraitRequest {
  name: string;
  description?: string;
  category?: TraitCategory;
  emoji?: string;
  spaceIds?: string[];
  pwfIds?: string[];
  parentTraitId?: string;
  hierarchy?: TraitHierarchy;
  level?: AssessmentLevel;
  personalDefinition?: string;
  personalExamples?: string[];
  developmentOpportunities?: string;
  isActive?: boolean;
  templateSlug?: string;
  tags?: string[];
  source?: TraitSource;
}

export interface UpdateTraitRequest {
  name?: string;
  description?: string;
  category?: TraitCategory;
  emoji?: string;
  hierarchy?: TraitHierarchy | null;
  level?: AssessmentLevel;
  personalDefinition?: string;
  personalExamples?: string[];
  developmentOpportunities?: string;
  isActive?: boolean;
  priority?: number | null;
  tags?: string[];
}

export interface AddEvidenceRequest {
  type: "insight" | "engagement" | "reflection" | "action_item" | "note";
  referenceId?: string;
  note?: string;
  relationship?: EvidenceRelationship;
  linkedBy?: "user" | "ai" | "system";
}

export interface TraitSuggestion {
  name: string;
  category: TraitCategory;
  reasoning: string;
}

export interface ImportFromLibraryRequest {
  templateSlugs: string[];
  spaceId?: string;
  pwfId?: string;
  includeChildren?: boolean;
}

export interface ImportFromLibraryResponse {
  success: boolean;
  traits: UserTrait[];
  count: number;
}

// ─── Trait CRUD ──────────────────────────────────────────────────────────────

/**
 * Create a new user trait
 */
export const createTrait = async (
  retryFetch: Function,
  data: CreateTraitRequest
): Promise<UserTrait> => {
  const response = await retryFetch(`${config.API_BASE}/api/user-traits`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to create trait");
  }

  return response.json();
};

/**
 * List traits for the current user, optionally filtered by space.
 * Pass tree=true to get a nested tree structure with computed rollups.
 * Pass active=true to get only actively developed traits.
 */
export const listTraits = async (
  retryFetch: Function,
  options?: { spaceId?: string; pwfId?: string; tree?: boolean; active?: boolean }
): Promise<UserTrait[] | UserTraitTreeNode[]> => {
  const params = new URLSearchParams();
  if (options?.spaceId) params.set("spaceId", options.spaceId);
  if (options?.pwfId) params.set("pwfId", options.pwfId);
  if (options?.tree) params.set("tree", "true");
  if (options?.active) params.set("active", "true");
  const query = params.toString() ? `?${params.toString()}` : "";

  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits${query}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch traits");
  }

  return response.json();
};

/**
 * Update a trait
 */
export const updateTrait = async (
  retryFetch: Function,
  id: string,
  data: UpdateTraitRequest
): Promise<UserTrait> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/${id}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to update trait");
  }

  return response.json();
};

/**
 * Delete a trait (cascades to children)
 */
export const deleteTrait = async (
  retryFetch: Function,
  id: string
): Promise<{ success: boolean; deletedCount: number }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/${id}`,
    {
      method: "DELETE",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to delete trait");
  }

  return response.json();
};

/**
 * Assess a trait — update its level and push to assessment history
 */
export const assessTrait = async (
  retryFetch: Function,
  id: string,
  level: AssessmentLevel,
  note?: string
): Promise<UserTrait> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/${id}/assess`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ level, note }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to assess trait");
  }

  return response.json();
};

/**
 * Reorder traits by priority (bulk)
 */
export const reorderTraits = async (
  retryFetch: Function,
  traitIds: string[]
): Promise<{ success: boolean }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/reorder`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ traitIds }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to reorder traits");
  }

  return response.json();
};

/**
 * Import traits from library templates into user's personal trait tree
 */
export const importFromLibrary = async (
  retryFetch: Function,
  data: ImportFromLibraryRequest
): Promise<ImportFromLibraryResponse> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/import`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to import from library");
  }

  return response.json();
};

/**
 * Link a trait to one or more spaces
 */
export const linkTraitToSpaces = async (
  retryFetch: Function,
  id: string,
  spaceIds: string[]
): Promise<UserTrait> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/${id}/spaces`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ spaceIds }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to link trait to spaces");
  }

  return response.json();
};

/**
 * Add evidence to a trait
 */
export const addEvidence = async (
  retryFetch: Function,
  id: string,
  data: AddEvidenceRequest
): Promise<UserTrait> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/${id}/evidence`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to add evidence");
  }

  return response.json();
};

/**
 * Get AI-suggested traits for a space
 */
export const suggestTraits = async (
  retryFetch: Function,
  spaceId: string
): Promise<{ suggestions: TraitSuggestion[] }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/ai/suggest-traits`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ spaceId }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to get trait suggestions");
  }

  return response.json();
};

/**
 * Get a single trait by ID (with children populated and totalEvidence count)
 */
export const getTrait = async (
  retryFetch: Function,
  id: string
): Promise<UserTrait> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/${id}`,
    {
      method: "GET",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to fetch trait");
  }

  return response.json();
};

/**
 * Delete a specific evidence entry from a trait
 */
export const deleteEvidence = async (
  retryFetch: Function,
  traitId: string,
  evidenceId: string
): Promise<UserTrait> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/${traitId}/evidence/${evidenceId}`,
    {
      method: "DELETE",
      credentials: "include",
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to delete evidence");
  }

  return response.json();
};

/**
 * Share a trait to an additional identity space
 */
export const shareTraitToSpace = async (
  retryFetch: Function,
  traitId: string,
  spaceId: string
): Promise<UserTrait> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/${traitId}/share`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ spaceId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to share trait to space");
  }

  return response.json();
};

/**
 * Suggest emojis for a list of trait names via AI
 */
export const suggestTraitEmojis = async (
  retryFetch: Function,
  traitNames: string[]
): Promise<{ emojis: Record<string, string> }> => {
  const response = await retryFetch(
    `${config.API_BASE}/api/user-traits/suggest-emoji`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ traitNames }),
    }
  );

  if (!response.ok) {
    throw new Error("Failed to suggest emojis");
  }

  return response.json();
};
